import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { NavigationPaths } from 'src/app/commons/config/navigation-paths.enum';
import { UserStatusEnum } from 'src/app/commons/config/user-status.enum';
import { IUser } from 'src/app/commons/interfaces/user.interface';
import { environment } from 'src/environments/environment';
import { StorageKeysEnum } from '../config/storage-keys.enum';
import { AuthHttp } from '../http/auth.http';
import { UserHttp } from '../http/user.http';
import { IAuthRequest } from '../interfaces/auth-request.interface';
import { IAuthResponse } from '../interfaces/auth-response.interface';

@Injectable()
export class SessionHandlerService {
  user$ = new BehaviorSubject<IUser>(null);

  constructor(private auth: AuthHttp, private router: Router) { }
  /**
   * login
   *
   * @description authenticate the user and store the token in the storage
   * @param body: { user_name: string, password: string }
   * @returns Observable with http login response
   */
  login(body: { username: string, password: string }): Observable<IAuthResponse> {
    const request: IAuthRequest = {
      username: body.username,
      password: body.password,
      client_id: environment.client_id,
      client_secret: environment.client_secret,
      grant_type: environment.grant_type,
      // scope: '*'

    };

    return this.auth.auth(request).pipe(
      map((response: IAuthResponse) => {
        this.saveToken(response);

        return response;
      })
    );
  }
  /**
   * closeSession
   *
   * @description clear the sessionStorage
   */
  closeSession(navigate = true): void {
    window.sessionStorage.clear();
    this.user$.next(null);
    if (navigate) {
      this.router.navigate([NavigationPaths.HOME]);
    }
  }
  /**
   * saveToken
   *
   * @description Store the user token in the sessionStorage
   *
   * @param auth user token
   */
  saveToken(auth: IAuthResponse): void {
    window.sessionStorage.setItem(StorageKeysEnum.AUTH, JSON.stringify(auth));
  }
  /**
   * getUser
   *
   * @description This method returns the user if authenticated, else null
   *
   * @returns IUser | null
   */
  getUser(): IUser {
    const userStored = window.sessionStorage.getItem(StorageKeysEnum.USER);
    const user: IUser = JSON.parse(userStored);
    return user;
  }
  /**
   * isUserEnrolled
   *
   * @description verify if user is enrolled
   *
   * @returns boolean
   */
  isUserEnrolled(): boolean {
    const user = this.getUser();
    return !!(user && user.status_id === UserStatusEnum.ENROLLED);
  }
  /**
   * isUserEnrolled
   *
   * @description verify if user is enrolled
   *
   * @returns boolean
   */
   hasPendingEnroll(): boolean {
    const user = this.getUser();

    return !!(user && user.status_id === UserStatusEnum.PENDING);
  }
  /**
   * isUserEnrolled
   *
   * @description verify if user has available enroll
   *
   * @returns boolean
   */
  isEnrollingAvailable(): boolean {
    const user = this.getUser();

    return !!(user && user.status_id === UserStatusEnum.REGISTERED);
  }
  isAuthenticated(): boolean {
    const authToken = window.sessionStorage.getItem(StorageKeysEnum.AUTH);
    const auth = JSON.parse(authToken);

    return !!auth;
  }

  setUserStatus(status: number): void  {
    const user = this.getUser();
    user.status_id = status;
  }

  setUser(user: IUser): void {
    const json = JSON.stringify(user);
    window.sessionStorage.setItem(StorageKeysEnum.USER, json);
    this.user$.next(user);
  }

  changeUserStatusToEnrolled(): void {
    const user = this.getUser();
    user.status_id = UserStatusEnum.ENROLLED;
    this.setUser(user);
  }

  getAuthToken(): IAuthResponse {
    const auth = window.sessionStorage.getItem('auth');
    const authToken: IAuthResponse = JSON.parse(auth);

    return authToken;
  }
}
